import { TooltipWrapper } from 'components/TooltipWrapper'
import { useRolodexEdit } from 'features/fde'
import { useKestrel } from 'features/kestrelViewer'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { ThermalGradientButton } from 'features/kestrelViewer/ui/tools/viewControls/ThermalGradientButton'
import { getDataLayersVisibility } from 'features/kestrelViewer/utils/visibilityUtils'
import {
  ArrowUp,
  Flame,
  Moon,
  Save,
  Sun,
  Thermometer,
  Trash2,
} from 'lucide-react'
import React from 'react'
import styled from 'styled-components'
import { KVDataLayersConfig, RolodexEntryPatchJson } from 'types'

const HotbarContainer = styled.div`
  display: flex;
  justify-items: center;
  flex-direction: row;
  position: absolute;
  left: 1.5rem;
  bottom: 1.1rem;
  gap: 0px;

  button {
    all: unset;
  }
`

export const LayerHotbar: React.FC = () => {
  const {
    activeLayer,
    setActiveLayer,
    sendCommand,
    dataLayersConfig,
    selectedMode,
    setSelectedMode,
    kestrelModel,
  } = useKestrelViewerContext()
  const { setKestrelModel } = useKestrel()
  const { rolodexPatch } = useRolodexEdit()

  const layers = React.useMemo(
    () => dataLayersConfig?.layers ?? [],
    [dataLayersConfig]
  )
  const enabled = React.useMemo(
    () => dataLayersConfig?.layerSwitchingEnabled ?? false,
    [dataLayersConfig]
  )
  const isMultiObjectModel = React.useMemo(
    () => dataLayersConfig?.isMultiObjectModel ?? false,
    [dataLayersConfig]
  )

  const hasDataLayersSupport = React.useMemo(
    () => getDataLayersVisibility(kestrelModel),
    [kestrelModel]
  )
  const isActiveRaw = activeLayer.type === 'raw'

  const KEYWORD_ICON_MAP: Record<string, React.ReactNode> = {
    ironbow: <Flame size={18} />,
    height: <ArrowUp size={18} />,
    thermal: <Thermometer size={18} />,
    day: <Sun size={18} />,
    night: <Moon size={18} />,
  }

  const getSourceIcon = (layerName: string) => {
    const lower = layerName.toLowerCase()
    for (const keyword of Object.keys(KEYWORD_ICON_MAP)) {
      if (lower.includes(keyword.toLowerCase())) {
        return KEYWORD_ICON_MAP[keyword]
      }
    }
    return null
  }

  const getSourceLayerName = (prefix: string) => {
    const sourceLayer = dataLayersConfig?.layers.find(
      (l) => l.prefix === prefix && !l.customId
    )
    return sourceLayer?.name || ''
  }

  const TYPE_TO_ABBR: Record<'rgb' | 'visual' | 'raw', string> = {
    rgb: 'RGB',
    visual: 'VIS',
    raw: 'RAW',
  }

  const getLayerSymbol = (name: string, type: 'rgb' | 'visual' | 'raw') => {
    const lower = name.toLowerCase()
    for (const keyword of Object.keys(KEYWORD_ICON_MAP)) {
      if (lower.includes(keyword.toLowerCase())) {
        return <LayerIconWrapper>{KEYWORD_ICON_MAP[keyword]}</LayerIconWrapper>
      }
    }
    return <LayerText>{TYPE_TO_ABBR[type]}</LayerText>
  }

  const selectLayer = React.useCallback(
    (layerConfig: { prefix: string; customId?: string }) => {
      const nextLayerConfig = dataLayersConfig?.layers.find(
        (l) =>
          l.prefix === layerConfig.prefix &&
          (layerConfig.customId
            ? l.customId === layerConfig.customId
            : !l.customId)
      )
      if (!nextLayerConfig) return

      const newLayer = {
        ...activeLayer,
        prefix: nextLayerConfig.prefix,
        type: nextLayerConfig.type,
        decode_min_f: nextLayerConfig.decodeRange?.min_f,
        decode_max_f: nextLayerConfig.decodeRange?.max_f,
        visible_min_f:
          nextLayerConfig.type === 'raw'
            ? (nextLayerConfig.savedVisibleRange?.min_f ??
              nextLayerConfig.decodeRange?.min_f)
            : undefined,
        visible_max_f:
          nextLayerConfig.type === 'raw'
            ? (nextLayerConfig.savedVisibleRange?.max_f ??
              nextLayerConfig.decodeRange?.max_f)
            : undefined,
        gradient: nextLayerConfig.savedGradient,
        opacity:
          nextLayerConfig.savedOpacity ??
          (nextLayerConfig.type === 'visual' ? 1.0 : activeLayer.opacity),
        isCustom: nextLayerConfig.isCustom,
        customId: nextLayerConfig.customId,
      } as const

      setActiveLayer(newLayer)

      if (newLayer.type === 'rgb') {
        sendCommand({
          command: 'showLayerRGB',
          prefix: newLayer.prefix,
          opacity: newLayer.opacity || 1.0,
          use_multi_obj_model: isMultiObjectModel,
        })
      } else if (newLayer.type === 'visual') {
        sendCommand({
          command: 'showLayerVisual',
          prefix: newLayer.prefix,
          opacity: newLayer.opacity || 1.0,
          use_multi_obj_model: isMultiObjectModel,
        })
      } else if (newLayer.type === 'raw') {
        const decodeMin = newLayer.decode_min_f ?? -100
        const decodeMax = newLayer.decode_max_f ?? 300

        const commandData = {
          command: 'showLayerRaw',
          prefix: newLayer.prefix,
          decode_min_f: decodeMin,
          decode_max_f: decodeMax,
          visible_min_f: newLayer.visible_min_f ?? decodeMin,
          visible_max_f: newLayer.visible_max_f ?? decodeMax,
          opacity: newLayer.opacity || 1.0,
          use_multi_obj_model: isMultiObjectModel,
        } as Record<string, unknown>

        if (
          newLayer.gradient?.control_points &&
          newLayer.gradient.control_points.length >= 2
        ) {
          const sortedPoints = [...newLayer.gradient.control_points].sort(
            (a, b) => a.temp_f - b.temp_f
          )
          commandData.gradient = {
            control_points: sortedPoints,
          }
        }

        sendCommand(commandData)
      }
    },
    [
      dataLayersConfig,
      activeLayer,
      setActiveLayer,
      sendCommand,
      isMultiObjectModel,
    ]
  )

  const handleTemperatureProbeClick = () => {
    if (selectedMode === 'temperature') {
      // Toggle off - switch to cursor mode
      setSelectedMode('cursor')
      sendCommand({ command: 'setMode', mode: 'cursor' })
      sendCommand({
        command: 'setPointerMode',
        createAnnotationOnClick: true,
      })
    } else {
      // Toggle on
      setSelectedMode('temperature')
      sendCommand({ command: 'setMode', mode: 'cursor' })
      sendCommand({
        command: 'setPointerMode',
        createAnnotationOnClick: false,
      })
    }
  }

  const handleDeleteCustomLayer = React.useCallback(async () => {
    if (!kestrelModel?.id || !dataLayersConfig || !activeLayer.customId) {
      return
    }

    // Find base layer before deleting
    const baseLayer = dataLayersConfig.layers.find(
      (l) => l.prefix === activeLayer.prefix && !l.customId
    )

    const updatedConfig = new KVDataLayersConfig()
    updatedConfig.layers = dataLayersConfig.layers.filter(
      (l) => l.customId !== activeLayer.customId
    )
    updatedConfig.layerSwitchingEnabled = dataLayersConfig.layerSwitchingEnabled

    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel.id,
      data: {
        dataLayersConfig: KVDataLayersConfig.toJSON(updatedConfig),
      },
    }

    try {
      await rolodexPatch([patchData])

      // Update local kestrelModel state
      if (kestrelModel) {
        setKestrelModel({
          ...kestrelModel,
          data: {
            ...kestrelModel.data,
            dataLayersConfig: updatedConfig,
          },
        })
      }

      // Switch to the base layer with same prefix
      if (baseLayer) {
        selectLayer({ prefix: baseLayer.prefix, customId: undefined })
      }
    } catch (error) {
      console.error('[LayerHotbar] Failed to delete custom layer:', error)
    }
  }, [
    kestrelModel,
    dataLayersConfig,
    activeLayer.customId,
    activeLayer.prefix,
    rolodexPatch,
    selectLayer,
    setKestrelModel,
  ])

  const isCustomLayerActive = !!activeLayer.isCustom

  React.useEffect(() => {
    if (!enabled || layers.length === 0) return

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target && target.isContentEditable)
      ) {
        return
      }

      const key = event.key
      if (key >= '1' && key <= '9') {
        const idx = Number(key) - 1
        if (idx < layers.length) {
          const layerAtIndex = layers[idx]
          selectLayer({
            prefix: layerAtIndex.prefix,
            customId: layerAtIndex.customId,
          })
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [enabled, layers, selectLayer])

  return (
    <HotbarContainer>
      {enabled &&
        layers.map((layer, index) => {
          const isSelected =
            layer.prefix === activeLayer.prefix &&
            layer.type === activeLayer.type &&
            (layer.customId
              ? layer.customId === activeLayer.customId
              : !activeLayer.customId)
          const tooltip = `${layer.name} (${layer.type.toUpperCase()})${layer.isCustom ? ' - Custom' : ''}`
          return (
            <TooltipWrapper
              key={layer.customId || layer.prefix}
              side='top'
              offset={5}
              label={tooltip}>
              <LayerButton
                $selected={isSelected}
                onClick={() =>
                  selectLayer({
                    prefix: layer.prefix,
                    customId: layer.customId,
                  })
                }>
                {layer.isCustom ? (
                  <LayerIconWrapper>
                    <Save size={18} />
                  </LayerIconWrapper>
                ) : (
                  getLayerSymbol(layer.name, layer.type)
                )}
                {layer.type === 'raw' && !layer.isCustom && (
                  <RawBadge>
                    <Thermometer size={18} />
                  </RawBadge>
                )}
                {layer.isCustom &&
                  getSourceIcon(getSourceLayerName(layer.prefix)) && (
                    <SourceBadge>
                      {getSourceIcon(getSourceLayerName(layer.prefix))}
                    </SourceBadge>
                  )}
                <IndexBadge>{index + 1}</IndexBadge>
              </LayerButton>
            </TooltipWrapper>
          )
        })}
      {isActiveRaw && <Spacer />}
      {isActiveRaw && <ThermalGradientButton />}
      {(() => {
        return hasDataLayersSupport && isActiveRaw
      })() && (
        <TooltipWrapper side='top' offset={5} label={'Temperature Probe'}>
          <LayerButton
            $selected={selectedMode === 'temperature'}
            onClick={handleTemperatureProbeClick}>
            <LayerIconWrapper>
              <Thermometer size={18} />
            </LayerIconWrapper>
          </LayerButton>
        </TooltipWrapper>
      )}
      {isCustomLayerActive && (
        <TooltipWrapper side='top' offset={5} label={'Delete Custom Layer'}>
          <DeleteLayerButton onClick={handleDeleteCustomLayer}>
            <LayerIconWrapper>
              <Trash2 size={18} />
            </LayerIconWrapper>
          </DeleteLayerButton>
        </TooltipWrapper>
      )}
    </HotbarContainer>
  )
}

const LayerButton = styled.div<{ $selected: boolean }>`
  cursor: pointer;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.forecast.background4 : theme.forecast.background1};
  opacity: 0.8;
  padding: 8px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: content-box;
  position: relative;

  &:hover {
    opacity: 1;
    background-color: ${({ theme, $selected }) =>
      $selected ? theme.forecast.background4 : theme.forecast.background3};
  }
`

const LayerText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 10px;
  color: white;
  width: 22px;
  height: 22px;
  text-transform: uppercase;
`

const LayerIconWrapper = styled.div`
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`

const IndexBadge = styled.div`
  position: absolute;
  bottom: 2px;
  right: 2px;
  font-size: 10px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.8);
  pointer-events: none;
`

const RawBadge = styled.div`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.9);
  pointer-events: none;
`

const Spacer = styled.div`
  width: 8px;
  height: 1px;
`

const DeleteLayerButton = styled.div`
  cursor: pointer;
  background-color: ${({ theme }) => theme.forecast.background1};
  opacity: 0.8;
  padding: 8px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: content-box;
  position: relative;
  color: #ef4444;

  &:hover {
    opacity: 1;
    background-color: ${({ theme }) => theme.forecast.background3};
    color: #dc2626;
  }
`

const SourceBadge = styled.div`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.9);
  pointer-events: none;
`
